import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fmtDate, fmtRupiah } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ESTIMATE_MANAGE, ESTIMATE_STATUSES, ESTIMATE_VIEW } from "@/lib/constants";
import { Loader2, PlusCircle, Search, ArrowRight, FileSpreadsheet } from "lucide-react";

const BADGE = { Draft: "bg-slate-100 text-slate-700", Diajukan: "bg-blue-50 text-blue-700", Disetujui: "bg-emerald-50 text-emerald-700", Ditolak: "bg-red-50 text-red-700", Terkirim: "bg-teal-50 text-teal-700" };

export default function Estimates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user && ESTIMATE_MANAGE.includes(user.role);
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const status = params.get("status") || "";

  const load = () => { setLoading(true); api.get("/estimates", { params: { ...(status ? { status } : {}), ...(q ? { q } : {}) } }).then(({ data }) => setList(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="space-y-4" data-testid="estimates-page">
      {user && !ESTIMATE_VIEW.includes(user.role) ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="access-denied">Akses Ditolak. Anda tidak memiliki hak melihat Estimasi/Penawaran.</Card>
      ) : (<>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl font-bold text-navy">Estimasi / Penawaran</h1><p className="text-sm text-muted-foreground">{list.length} penawaran</p></div>
        {canManage && <Button onClick={() => navigate("/estimates/new")} data-testid="new-estimate-btn" className="bg-primary hover:bg-red-700"><PlusCircle size={16} className="mr-1.5" /> Buat Penawaran</Button>}
      </div>
      <Card className="p-3 flex flex-col sm:flex-row gap-2">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no. penawaran, proyek, customer, no. polisi..." className="pl-9" data-testid="estimate-search" />
        </form>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setParams({})} className={`px-3 py-1.5 rounded-full text-xs border ${!status ? "bg-navy text-white border-navy" : "bg-white"}`}>Semua</button>
          {ESTIMATE_STATUSES.map((s) => <button key={s} data-testid={`est-filter-${s}`} onClick={() => setParams({ status: s })} className={`px-3 py-1.5 rounded-full text-xs border ${status === s ? "bg-navy text-white border-navy" : "bg-white"}`}>{s}</button>)}
        </div>
      </Card>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : (
        list.length === 0 ? <Card className="p-10 text-center text-muted-foreground"><FileSpreadsheet className="mx-auto mb-2 opacity-40" /> Belum ada penawaran.</Card> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((e) => (
              <Card key={e.id} data-testid={`estimate-card-${e.estimate_no}`} className="p-4 space-y-1.5 cursor-pointer hover:shadow-md" onClick={() => navigate(`/estimates/${e.id}`)}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-k3 text-xs font-semibold text-primary">{e.estimate_no}{e.revision > 0 && <span className="ml-1 text-amber-600">rev.{e.revision}</span>}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${BADGE[e.status]}`}>{e.status}</span>
                </div>
                <div className="font-semibold">{e.plate} · {e.customer_name}</div>
                <div className="text-xs text-muted-foreground">{e.project_id} · {e.work_type}</div>
                <div className="text-sm font-bold text-navy">{fmtRupiah(e.grand_total)}</div>
                <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{fmtDate(e.date)}</span><ArrowRight size={14} /></div>
              </Card>
            ))}
          </div>
        )
      )}
      </>)}
    </div>
  );
}
